import {createField, normalizeFieldSubgroupMeta, normalizeListing} from './utils';

const CONFIG_SCHEMA_VERSION = 2;

const toLookupKey = (value = '') => String(value || '').trim().toLowerCase();

const buildProductLookup = (products = new Map()) => {
    const productList = products instanceof Map ? Array.from(products.values()) : products;
    const lookup = new Map();

    productList.filter(Boolean).forEach((product) => {
        [
            product.id,
            product.sku,
            product.shopifyId,
            product.variantId,
            product?.raw?.variant?.sku,
            product?.raw?.variant_id,
        ].forEach((value) => {
            const key = toLookupKey(value);
            if (key && !lookup.has(key)) lookup.set(key, product);
        });
    });

    return lookup;
};

export const buildSavableReviewConfigGroups = (listings = []) => {
    const groupsByKey = new Map();

    listings.forEach((listing) => {
        if (!listing?.superSku) return;
        if (!groupsByKey.has(listing.superSku)) {
            groupsByKey.set(listing.superSku, []);
        }
        groupsByKey.get(listing.superSku).push(listing);
    });

    return Array.from(groupsByKey.entries())
        .map(([groupKey, groupListings]) => ({
            groupKey,
            listings: groupListings,
        }))
        .filter((group) => group.listings.length >= 2);
};

const buildListingSkuList = (groupListings = []) => {
    const seen = new Set();
    const skus = [];

    groupListings.forEach((listing) => {
        const sku = (listing?.sku || listing?.sourceProductId || '').toString().trim();
        if (!sku || seen.has(sku)) return;
        seen.add(sku);
        skus.push(sku);
    });

    return skus;
};

// Each in-memory listing field is one logical-field-instance. Across the
// group, instances aligned by (type, ordinal-within-type) represent the
// SAME logical field — possibly with subgroup-specific content.
const collectGroupFieldSlots = (groupListings = []) => {
    const slots = new Map();
    const orderedSlotKeys = [];

    groupListings.forEach((listing, listingIndex) => {
        const ordinalsByType = new Map();
        (listing.fields || []).forEach((field) => {
            if (!field || !field.type) return;
            const ordinal = ordinalsByType.get(field.type) || 0;
            ordinalsByType.set(field.type, ordinal + 1);

            const slotKey = `${field.type}:${ordinal}`;
            if (!slots.has(slotKey)) {
                slots.set(slotKey, {
                    type: field.type,
                    ordinal,
                    instances: [],
                });
                orderedSlotKeys.push(slotKey);
            }
            slots.get(slotKey).instances.push({listingIndex, field});
        });
    });

    return orderedSlotKeys.map((key) => slots.get(key));
};

const buildContentSignature = (field = {}) =>
    JSON.stringify({
        text: typeof field.text === 'string' ? field.text : '',
        question:
            field.type === 'faq' && typeof field.question === 'string'
                ? field.question
                : '',
        status: field.status || 'draft',
        searchTerms: Array.isArray(field.searchTerms)
            ? [...field.searchTerms].sort()
            : [],
        ignoredIssueIds: Array.isArray(field.ignoredIssueIds)
            ? [...field.ignoredIssueIds].sort()
            : [],
    });

const buildSharedContentEntry = (instance) => {
    const {field} = instance;
    return {
        text: typeof field.text === 'string' ? field.text : '',
        ...(field.type === 'faq'
            ? {question: typeof field.question === 'string' ? field.question : ''}
            : {}),
        status: field.status || 'draft',
        searchTerms: Array.isArray(field.searchTerms)
            ? field.searchTerms.filter(Boolean)
            : [],
        ignoredIssueIds: Array.isArray(field.ignoredIssueIds)
            ? field.ignoredIssueIds.filter(Boolean)
            : [],
    };
};

const buildSubgroupContentEntries = (instances = []) => {
    const bySubgroupKey = new Map();

    instances.forEach((instance) => {
        const meta = normalizeFieldSubgroupMeta(instance.field?.subgroupMeta);
        const subgroupKey = meta?.groupKey || '';
        if (bySubgroupKey.has(subgroupKey)) return;

        bySubgroupKey.set(subgroupKey, {
            uniqueVarNames: meta?.uniqueVarNames || [],
            valuesByName: meta?.valuesByName || {},
            ...buildSharedContentEntry(instance),
        });
    });

    return Array.from(bySubgroupKey.values());
};

const serializeFieldSlot = (slot) => {
    const instances = slot.instances || [];
    if (!instances.length) return null;

    const templateField = instances[0].field;
    const baseEntry = {
        id: templateField.id || '',
        type: slot.type,
        label: templateField.label || '',
    };

    const signatures = new Set(instances.map(({field}) => buildContentSignature(field)));

    if (signatures.size === 1) {
        return {
            ...baseEntry,
            ...buildSharedContentEntry(instances[0]),
        };
    }

    return {
        ...baseEntry,
        bySubgroup: buildSubgroupContentEntries(instances),
    };
};

export const buildReviewConfigPayload = ({
    name = '',
    listings = [],
} = {}) => ({
    version: CONFIG_SCHEMA_VERSION,
    name: name.trim(),
    groups: buildSavableReviewConfigGroups(listings).map((group) => ({
        groupKey: group.groupKey,
        skus: buildListingSkuList(group.listings),
        fields: collectGroupFieldSlots(group.listings)
            .map(serializeFieldSlot)
            .filter(Boolean),
    })),
});

// ── Hydration ──────────────────────────────────────────────────────────

const subgroupEntryMatchesProductVars = (entry, productVarsByName) => {
    const valuesByName = entry.valuesByName || {};
    const names = entry.uniqueVarNames?.length
        ? entry.uniqueVarNames
        : Object.keys(valuesByName);
    if (!names.length) return false;

    return names.every((name) => {
        const expected = String(valuesByName[name] || '').trim();
        const actual = String(productVarsByName.get(name) || '').trim();
        return expected === actual;
    });
};

const resolveProductVarsByName = (product) => {
    const productVars = Array.isArray(product?.vars) ? product.vars : [];
    const byName = new Map();
    productVars.forEach((entry) => {
        if (entry?.name) {
            byName.set(entry.name, entry.sample ?? '');
        }
    });
    return byName;
};

const buildFieldForProduct = (savedField, product) => {
    const productVarsByName = resolveProductVarsByName(product);
    const subgroupEntries = Array.isArray(savedField.bySubgroup)
        ? savedField.bySubgroup
        : null;

    let content;
    let subgroupMeta = null;

    if (subgroupEntries && subgroupEntries.length) {
        const matched =
            subgroupEntries.find((entry) =>
                subgroupEntryMatchesProductVars(entry, productVarsByName),
            ) || subgroupEntries[0];
        content = matched;
        subgroupMeta = normalizeFieldSubgroupMeta({
            uniqueVarNames: matched.uniqueVarNames,
            valuesByName: matched.valuesByName,
        });
    } else {
        content = savedField;
    }

    return createField({
        id: undefined,
        history: [],
        type: savedField.type || 'bullet',
        label: savedField.label || '',
        text: typeof content?.text === 'string' ? content.text : '',
        ...(savedField.type === 'faq'
            ? {
                  question:
                      typeof content?.question === 'string' ? content.question : '',
              }
            : {}),
        status: content?.status || 'draft',
        searchTerms: Array.isArray(content?.searchTerms)
            ? content.searchTerms.filter(Boolean)
            : [],
        ignoredIssueIds: Array.isArray(content?.ignoredIssueIds)
            ? content.ignoredIssueIds.filter(Boolean)
            : [],
        subgroupMeta,
    });
};

// Convert a legacy v1 group (per-listing `fields[]` with optional
// per-sibling overrides) into the v2 slot shape so the rest of the
// hydrate path is uniform. Picks the first listing that actually carries
// fields as the template, and treats any sibling with its own fields as
// a per-subgroup override.
const adaptLegacyGroupToV2 = (group = {}) => {
    const savedListings = Array.isArray(group.listings) ? group.listings : [];
    const templateListing = savedListings.find(
        (entry) => Array.isArray(entry?.fields) && entry.fields.length,
    );
    if (!templateListing) {
        return {
            groupKey: group.groupKey || '',
            skus: savedListings
                .map((listing) => (listing?.sku || listing?.sourceProductId || '').toString())
                .filter(Boolean),
            fields: [],
        };
    }

    const templateFields = templateListing.fields || [];

    return {
        groupKey: group.groupKey || '',
        skus: savedListings
            .map((listing) => (listing?.sku || listing?.sourceProductId || '').toString())
            .filter(Boolean),
        fields: templateFields.map((field) => ({
            id: field.id || '',
            type: field.type || 'bullet',
            label: field.label || '',
            text: typeof field.text === 'string' ? field.text : '',
            ...(field.type === 'faq'
                ? {question: typeof field.question === 'string' ? field.question : ''}
                : {}),
            status: field.status || 'draft',
            searchTerms: Array.isArray(field.searchTerms) ? field.searchTerms : [],
            ignoredIssueIds: Array.isArray(field.ignoredIssueIds)
                ? field.ignoredIssueIds
                : [],
        })),
    };
};

const groupLooksLikeV2 = (group = {}) =>
    Array.isArray(group?.skus) || Array.isArray(group?.fields);

export const hydrateReviewConfig = ({
    config = null,
    products = new Map(),
} = {}) => {
    const lookup = buildProductLookup(products);
    const missingSkus = [];
    const skippedGroups = [];
    const listings = [];
    const selectedProductIds = [];
    const reviewGroupByProductId = {};

    const rawGroups = Array.isArray(config?.groups) ? config.groups : [];
    const groups = rawGroups.map((group) =>
        groupLooksLikeV2(group) ? group : adaptLegacyGroupToV2(group),
    );

    groups.forEach((group) => {
        const groupSkus = Array.isArray(group.skus) ? group.skus : [];
        const groupFields = Array.isArray(group.fields) ? group.fields : [];
        const hydratedGroupListings = [];

        groupSkus.forEach((sku) => {
            const product = lookup.get(toLookupKey(sku));
            if (!product?.id) {
                missingSkus.push(sku);
                return;
            }

            reviewGroupByProductId[product.id] = group.groupKey;
            if (!selectedProductIds.includes(product.id)) {
                selectedProductIds.push(product.id);
            }

            hydratedGroupListings.push(
                normalizeListing({
                    id: `p-${product.id}`,
                    name: product.name || product.sku || product.id,
                    sku: product.sku || sku,
                    category: product.category || '',
                    superSku: group.groupKey,
                    sourceProductId: product.id,
                    // Keep a per-SKU snapshot key so batch rewrite jobs
                    // don't collapse independently resolved config fields
                    // into one request. Manual group edits link by superSku
                    // and intentionally ignore this key.
                    editLinkKey: `config-snapshot:${group.groupKey}:${product.id}`,
                    fields: groupFields.map((field) => buildFieldForProduct(field, product)),
                }),
            );
        });

        if (hydratedGroupListings.length < 2) {
            skippedGroups.push(group.groupKey);
            hydratedGroupListings.forEach((listing) => {
                delete reviewGroupByProductId[listing.sourceProductId];
                const selectedIndex = selectedProductIds.indexOf(listing.sourceProductId);
                if (selectedIndex >= 0) selectedProductIds.splice(selectedIndex, 1);
            });
            return;
        }

        listings.push(...hydratedGroupListings);
    });

    return {
        listings,
        missingSkus: Array.from(new Set(missingSkus)),
        reviewGroupByProductId,
        selectedProductIds,
        skippedGroups: Array.from(new Set(skippedGroups)),
    };
};
